import { createPaginatedFetcher } from '@/@core/libs/fetch/usePaginatedFetcher'

/**
 * API function untuk mengambil data produk dari backend
 * Response format: { meta: {...}, data: [...] }
 * @param {Object} http - HTTP client instance
 * @param {Function} onError - Error handler callback
 * @param {Object} params - Query parameters
 * @param {number} params.offset - Offset untuk pagination
 * @param {number} params.limit - Limit per page
 * @param {string} params.keyword - Keyword untuk search
 * @returns {Promise<{data: Array}>} Promise yang resolve dengan data array
 */
async function getProductsData(http, onError, {
  offset = 0, limit = 50, keyword = '', warehouseId = undefined, is_variant = 0,
} = {}, endpoint) {
  try {
    const response = await http.get(endpoint, {
      params: {
        page: offset,
        limit,
        keyword: keyword || undefined,
        warehouse_id: warehouseId,
        is_variant,
      },
    })

    const { data } = response.data
    const items = Array.isArray(data) ? data : []

    return {
      data: items,
    }
  } catch (error) {
    console.error(error)
    onError('Gagal mengambil data produk, silahkan coba lagi')
    throw error
  }
}

/**
 * Composable untuk manage product list dengan pagination dan search
 * @param {Object} options - Configuration options
 * @param {Object} options.http - HTTP client instance
 * @param {Function} options.onError - Error handler callback
 * @returns {Object} Object berisi methods untuk manage product list
 */
const useProductList = ({
  http,
  onError,
  endpoint = '/komship/api/v1/products/kompack/summaries',
} = {}) => {
  /**
   * Create paginated fetcher untuk products
   * @returns {Object} Paginated fetcher instance
   */
  const createProductsFetcher = () => {
    const fnFetch = params => getProductsData(http, onError, params, endpoint)

    return createPaginatedFetcher(fnFetch, 0, 50)
  }

  /**
   * Prepare products with is_connected flag
   * @param {Array} products - Array of products
   * @param {string|number} activeProductId - ID produk yang sedang aktif/terhubung
   * @returns {Array} Array of products with is_connected flag
   */
  const prepareProducts = (products = [], activeProductId = null) => {
    if (!Array.isArray(products)) {
      return []
    }

    return products.map(product => ({
      ...product,
      is_connected: product.id === activeProductId,
    }))
  }

  return {
    createProductsFetcher,
    prepareProducts,
  }
}

export default useProductList
